import { observer } from "mobx-react";
import {
  Button,
  ButtonGroup,
  Checkbox,
  HStack,
  Progress,
  Spacer,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { nodeReferences } from "../../../../../cra/stores/CraNodeStore";
import { NodeItem } from "./NodeItem";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { scrollToItem } from "../../../../../shared/utils/scroll-utils";
import { RouteMatchingControls } from "./RouteMatchingControls";
import { PageControls } from "./PageControls";
import { CopyNodesModal } from "./CopyNodesModal/CopyNodesModal";
import { useStores } from "../../../../../shared/stores/RootStore";
import { ProgressState } from "../../../../../shared/models/JobState";
import { NodePhotoModal } from "../../../../../shared/views/NodePhotoModal/NodePhotoModal";
import { MapMode } from "../../../../../survey/stores/MapStore";
import { toast } from "../../../../../shared/views/CustomToaster";

const i18prefix = "map:bottomBar.craWay";
const pageSize = 20;
const listId = "nodeList";

const NodeListBase: React.FC = () => {
  const { craWayStore, craNodeStore, mapStore } = useStores();
  const craWay = craWayStore.getSelected()!;
  const craNodes = craNodeStore.nodesByWayId.get(craWay.id!)!;
  const selectedCraNode = craNodeStore.getSelected();
  const copyCraNodeModal = useDisclosure();
  const craNodePhotoModal = useDisclosure();

  const [pageIndex, setPageIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletionProgress, setDeletionProgress] = useState<ProgressState>();
  const [flipProgress, setFlipProgress] = useState<ProgressState>();
  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);

  const firstItemIndex = pageIndex * pageSize;
  const lastItemIndex = Math.min(firstItemIndex + pageSize, craNodes.length);

  const page = craNodes.slice(firstItemIndex, lastItemIndex);

  useEffect(() => {
    if (craWay.permissions.canUpdate) {
      mapStore.setMode(MapMode.ManageNodes);
    }
    return () => {
      mapStore.setMode(MapMode.SurveyPage);
      craNodeStore.clearPendingCoordChanges();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setPageIndex(0);
    // eslint-disable-next-line
  }, [craWay]);

  useEffect(() => {
    if (selectedCraNode && page.find((n) => n.id === selectedCraNode.id)) {
      scrollToItem(selectedCraNode.id!);
    } else {
      scrollToItem(page[0]?.id);
    }
    // eslint-disable-next-line
  }, [pageIndex, selectedCraNode]);

  useEffect(() => {
    if (selectedCraNode) {
      const nodeReference = nodeReferences.get(selectedCraNode.id)!;
      if (
        nodeReference.index < firstItemIndex ||
        nodeReference.index >= lastItemIndex
      ) {
        setPageIndex(Math.floor(nodeReference.index / pageSize));
      }
    }
    // eslint-disable-next-line
  }, [selectedCraNode]);

  useEffect(() => {
    craNodeStore.setMultiSelectedIds(new Set());
    setLastClickedIndex(null);
    // eslint-disable-next-line
  }, [craWay.id]);

  const selectAll = (value: boolean) => {
    if (value) {
      craNodeStore.setMultiSelectedIds(new Set(craNodes.map((n) => n.id)));
    } else {
      craNodeStore.setMultiSelectedIds(new Set());
    }
  };

  const updateRangeSelection = (
    fromIndex: number,
    toIndex: number,
    checked: boolean,
  ) => {
    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);

    const newSet = new Set(craNodeStore.multiSelectedIds);

    for (let i = start; i <= end; i++) {
      const node = craNodes[i];
      if (!node) continue;

      if (checked) newSet.add(node.id);
      else newSet.delete(node.id);
    }

    craNodeStore.setMultiSelectedIds(newSet);
  };

  const handleNodeCheckboxChange = (
    nodeId: string,
    globalIndex: number,
    checked: boolean,
    shiftKey: boolean,
  ) => {
    if (shiftKey && lastClickedIndex !== null) {
      updateRangeSelection(lastClickedIndex, globalIndex, checked);
    } else {
      const newSet = new Set(craNodeStore.multiSelectedIds);
      if (checked) newSet.add(nodeId);
      else newSet.delete(nodeId);
      craNodeStore.setMultiSelectedIds(newSet);
    }

    setLastClickedIndex(globalIndex);
  };

  const deleteSelectedNodes = () => {
    if (window.confirm(t(`${i18prefix}.deleteConfirm`))) {
      setIsSubmitting(true);

      const totalNodes = craNodeStore.multiSelectedIds.size;
      let deletedNodes = 0;

      setDeletionProgress({
        value: deletedNodes,
        total: totalNodes,
      });

      craNodeStore
        .deleteSelectedNodes(() => {
          deletedNodes++;
          setDeletionProgress({ value: deletedNodes, total: totalNodes });
        })
        .then(() => {
          craWayStore.compileCraWayNodes(craWay.id);
        })
        .finally(() => {
          setIsSubmitting(false);
          setDeletionProgress(undefined);
        });
    }
  };

  const flipSelectedNodes = () => {
    setIsSubmitting(true);

    const totalNodes = craNodeStore.multiSelectedIds.size;
    let processedNodes = 0;

    setFlipProgress({
      value: processedNodes,
      total: totalNodes,
    });

    craNodeStore
      .flipSelectedNodes(() => {
        processedNodes++;
        setFlipProgress({ value: processedNodes, total: totalNodes });
      })
      .then(() => {
        toast("success", "Photos saved");
      })
      .catch(() => {
        toast("error", "An error occurred. You may want to refresh the page.");
      })
      .finally(() => {
        setIsSubmitting(false);
        setFlipProgress(undefined);
      });
  };

  return (
    <Stack gap={0} flexShrink={0}>
      <HStack p={2} pb={0}>
        <HStack>
          <Checkbox.Root
            p={2}
            checked={craNodeStore.multiSelectedIds.size === craNodes.length}
            onCheckedChange={(e: any) => {
              selectAll(!!e.checked);
            }}
            colorPalette="primary"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>
              <Text fontSize={"sm"}>
                {t(`${i18prefix}.nodesSelected`, {
                  count: craNodeStore.multiSelectedIds.size,
                })}
              </Text>
            </Checkbox.Label>
          </Checkbox.Root>

          <RouteMatchingControls />

          <ButtonGroup
            colorPalette="primary"
            variant="outline"
            size="sm"
            fontSize={"sm"}
          >
            <Button
              disabled={!craNodeStore.multiSelectedIds.size}
              onClick={flipSelectedNodes}
              loading={isSubmitting}
            >
              Flip
            </Button>

            <Button
              disabled={!craNodeStore.multiSelectedIds.size}
              onClick={copyCraNodeModal.onOpen}
              loading={isSubmitting}
            >
              {t(`${i18prefix}.copy`)}
            </Button>

            <Button
              disabled={!craNodeStore.multiSelectedIds.size}
              loading={isSubmitting}
              onClick={deleteSelectedNodes}
              colorPalette={"red"}
            >
              {t(`${i18prefix}.delete`)}
            </Button>
          </ButtonGroup>

          {!!deletionProgress && (
            <ProgressBar progressState={deletionProgress} />
          )}

          {!!flipProgress && <ProgressBar progressState={flipProgress} />}
        </HStack>
        <Spacer />

        <PageControls
          pageSize={pageSize}
          nItems={craNodes.length}
          setPageIndex={setPageIndex}
          pageIndex={pageIndex}
        />
      </HStack>

      <HStack flexGrow={1} overflow="auto" p={2} id={listId}>
        {page.map((craNode, idxInPage) => {
          const globalIndex = firstItemIndex + idxInPage;

          return (
            <NodeItem
              key={craNode.id}
              craNode={craNode}
              index={globalIndex}
              isChecked={craNodeStore.multiSelectedIds.has(craNode.id)}
              onCheckboxChange={handleNodeCheckboxChange}
              expandPhoto={() => craNodePhotoModal.onOpen()}
            />
          );
        })}
      </HStack>

      {copyCraNodeModal.open && (
        <CopyNodesModal onClose={copyCraNodeModal.onClose} />
      )}
      {craNodePhotoModal.open && (
        <NodePhotoModal onClose={craNodePhotoModal.onClose} />
      )}
    </Stack>
  );
};

export const NodeList = observer(NodeListBase);

const ProgressBar = ({
  progressState,
}: {
  progressState: ProgressState | undefined;
}) => {
  return (
    <Progress.Root
      width={"140px"}
      size={"md"}
      value={progressState?.value ?? null}
      max={progressState?.total}
      striped
      animated
      colorPalette="red"
    >
      <Progress.Track>
        <Progress.Range />
      </Progress.Track>
      {!!progressState && (
        <Progress.ValueText>
          {progressState.value} / {progressState.total}
        </Progress.ValueText>
      )}
    </Progress.Root>
  );
};
